import React from 'react'
import { Col, Button, Table, Empty, Space, Skeleton, theme, Row, Card, Input, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh, faPlus, faSearch, faEdit } from '@fortawesome/free-solid-svg-icons'
import Templates from '../utils/templates'
import { sortBy } from 'lodash'
import CustomButton from '../../../reusable-components/CustomButton'
import CustomRow from '../../../reusable-components/CustomRow'
import { useDispatch, useSelector } from 'react-redux'
import coreReducer from '../../../../core/utils/reducer'
import useServices from '../hooks/useServices'

// Add spin animation with more refined keyframes
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(360deg); }
  }
  
  .ant-table-row:hover .service-icon {
    transform: scale(1.1);
    transition: all 0.2s ease;
  }
  
  .service-icon {
    transition: all 0.2s ease;
  }
  
  .ant-table-row {
    transition: all 0.3s ease;
  }
  
  .ant-table-row:hover {
    background-color: rgba(24, 144, 255, 0.05) !important;
  }
`

const ServicesTable = () => {
  const coreState = useSelector((state) => state.core)
  const dispatch = useDispatch()
  const { token } = theme.useToken()

  const { loading, viewData, refreshView, createRecord, editRecord, filters, handleDebouncedSearch } = useServices()

  // Function to generate skeleton rows - optimized
  const generateSkeletonRows = () => {
    return Array(5)
      .fill()
      .map((_, i) => ({
        key: `skeleton-${i}`,
        _id: `skeleton-${i}`,
        skeleton: true
      }))
  }

  // Function to generate skeleton columns - optimized
  const skeletonColumns = () => {
    // Get columns and filter out the action column (it's the first one)
    let columns = Templates.columnTemplate(filters, editRecord)
      .filter((col) => col.key !== 'action')
      .map((col) => ({
        ...col,
        render: () => <Skeleton.Input active size='small' style={{ width: '100%' }} />,
        onCell: () => ({
          style: { cursor: 'default' }
        })
      }))

    // Add actions column at the end (fixed to right)
    columns.push({
      title: <h2>Actions</h2>,
      key: 'actions',
      align: 'center',
      fixed: 'right',
      width: 100,
      render: () => <Skeleton.Input active size='small' style={{ width: '100%' }} />
    })

    return columns
  }

  return (
    <>
      <style>{spinAnimation}</style>
      <Card bordered={false} className='services-list-card' bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ marginBottom: '12px' }}>
          <Row gutter={[0, 0]} align='middle' justify='space-between' wrap>
            <Col xs={24} sm={14} md={16} lg={16} xl={16}>
              <Input
                onChange={(e) => handleDebouncedSearch(e.target.value)}
                placeholder='Search for Service Name'
                prefix={
                  <div
                    style={{
                      background: token.actionColor,
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faSearch} style={{ color: 'white', fontSize: '12px' }} />
                  </div>
                }
                allowClear
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  borderRadius: '6px',
                  height: '40px'
                }}
                disabled={loading}
                size='middle'
              />
            </Col>
            <Col xs={24} sm={10} md={8} lg={8} xl={8}>
              <Row gutter={[12, 8]} justify='end'>
                <Col>
                  <Tooltip title='Refresh services list'>
                    <Button
                      icon={
                        <FontAwesomeIcon
                          icon={faRefresh}
                          style={{
                            color: token.actionColor,
                            animation: loading ? 'spin 1s linear infinite' : 'none',
                            marginRight: 8
                          }}
                        />
                      }
                      onClick={() => refreshView()}
                      disabled={loading}
                      type='default'
                      size='large'
                      style={{
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '40px',
                        borderRadius: '6px',
                        background: 'rgba(24, 144, 255, 0.1)',
                        borderColor: 'rgba(24, 144, 255, 0.3)',
                        color: token.actionColor
                      }}
                    >
                      Refresh
                    </Button>
                  </Tooltip>
                </Col>
                <Col>
                  <Button
                    id='service_add_btn'
                    onClick={() => {
                      createRecord()
                      if (coreState.tourOpen) {
                        dispatch(coreReducer.actions.nextTourStep())
                      }
                    }}
                    disabled={loading}
                    type='primary'
                    style={{
                      background: loading ? undefined : token.actionColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      height: '40px',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                      borderRadius: '6px'
                    }}
                    size='large'
                    icon={<FontAwesomeIcon icon={faPlus} />}
                  >
                    Add Service
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        <Table
          size='small'
          bordered
          id='services_list_view'
          loading={false} // We handle loading state ourselves
          rowKey={(record) => record._id}
          dataSource={loading ? generateSkeletonRows() : sortBy(viewData, 'name')}
          pagination={{
            position: ['bottomRight'],
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} services`,
            size: 'default'
          }}
          scroll={{ x: 950 }}
          columns={
            loading
              ? skeletonColumns()
              : [
                  ...Templates.columnTemplate(filters, editRecord).filter((col) => col.key !== 'action'),
                  {
                    title: <h2>Actions</h2>,
                    key: 'actions',
                    align: 'center',
                    fixed: 'right',
                    width: 100,
                    render: (_, record) => (
                      <Tooltip title='Edit service details'>
                        <Button
                          type='primary'
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation() // Prevent row click event
                            if (typeof editRecord === 'function') {
                              editRecord(record)
                            }
                          }}
                          icon={<FontAwesomeIcon icon={faEdit} />}
                          style={{
                            height: '32px',
                            width: '32px',
                            minWidth: '32px',
                            background: token.actionColor,
                            borderRadius: '50%',
                            boxShadow: `0 2px 4px ${token.colorPrimaryBg}`,
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                        />
                      </Tooltip>
                    )
                  }
                ]
          }
          onRow={(record) => ({
            style: {
              cursor: 'default',
              transition: 'background-color 0.3s ease'
            }
          })}
          locale={{
            emptyText: loading ? (
              <div style={{ padding: '40px 0' }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </div>
            ) : (
              <Empty
                description='There are currently no services to show.'
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '40px 0' }}
              />
            )
          }}
          rowClassName={(record) => (record.skeleton ? 'skeleton-row' : '')}
        />
      </Card>
    </>
  )
}

export default ServicesTable
